import {
  FunctionComponent, Children, isValidElement, ReactElement, useState, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import { PaneviewReact } from 'dockview-react';
import type { PaneviewReadyEvent, PaneviewApi } from 'dockview-react';
import { NavigationHeader } from '@cbridge-tools/navigation-block';
import { ClearFilterButton } from '../ClearFilterButton/ClearFilterButton';
import { FilterPanelBody } from './FilterPanelBody';
import { StyledPaneviewContainer } from './StyledPaneviewContainer';
import { IFiltersPanelProps } from './FiltersPanel.interface';
import { SINGLE_FILTER_HEADER_HEIGHT } from '../../FiltersPanel.constant';

const SPACER_PANEL_ID = 'spacer';

function PaneviewSpacer() {
  return <div className={'paneview-spacer'} />;
}

const paneviewComponents = {
  filterBody: FilterPanelBody,
  spacer: PaneviewSpacer,
};

export const FiltersPanel: FunctionComponent<IFiltersPanelProps> = (props) => {
  const { children, onClearAllFilters, hasActiveFilters, headerActions } = props;

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [paneviewApi, setPaneviewApi] = useState<PaneviewApi | null>(null);

  const childrenArray = useMemo<ReactElement[]>(() => {
    if (!children) {
      return [];
    }
    return Children.map(children, (child) => child)?.filter(isValidElement) ?? [];
  }, [children]);

  const [sashUpdateTrigger, setSashUpdateTrigger] = useState(0);

  const onReady = useCallback((event: PaneviewReadyEvent) => {
    setPaneviewApi(event.api);
  }, []);

  // Tracks which panels are collapsed by their actual isCollapsed state (not by current height)
  const collapsedSetRef = useRef(new Set<string>());

  // Distribute available space equally among expanded panels, preventing spacer from absorbing it
  const redistributeExpandedPanels = useCallback(() => {
    if (!paneviewApi) {
      return;
    }

    const filterPanels = paneviewApi.panels.filter((p) => p.id !== SPACER_PANEL_ID);
    // Use collapsedSetRef instead of height check — a panel may be temporarily shrunk by dockview but still logically expanded
    const expandedPanels = filterPanels.filter((p) => !collapsedSetRef.current.has(p.id));

    if (expandedPanels.length > 0) {
      const totalHeight = paneviewApi.height;
      const collapsedCount = filterPanels.length - expandedPanels.length;
      const collapsedSpace = collapsedCount * SINGLE_FILTER_HEADER_HEIGHT;
      const availableForExpanded = totalHeight - collapsedSpace;
      const sizePerPanel = Math.floor(availableForExpanded / expandedPanels.length);

      expandedPanels.forEach((panel) => {
        panel.api.setSize({ size: sizePerPanel });
      });
    }
  }, [paneviewApi]);

  const isProgrammaticResizeRef = useRef(false);

  const onCollapseChange = useCallback((panelId: string, collapsed: boolean) => {
    if (collapsed) {
      collapsedSetRef.current.add(panelId);
    } else {
      collapsedSetRef.current.delete(panelId);
    }

    isProgrammaticResizeRef.current = true;
    redistributeExpandedPanels();
    setSashUpdateTrigger((prev) => prev + 1);
    requestAnimationFrame(() => {
      isProgrammaticResizeRef.current = false;
    });
  }, [redistributeExpandedPanels]);

  useEffect(() => {
    if (!paneviewApi) {
      return;
    }

    const filterPanels = paneviewApi.panels.filter((p) => p.id !== SPACER_PANEL_ID);
    const currentFilterCount = filterPanels.length;
    const newCount = childrenArray.length;

    // Update params for existing panels that are still present
    for (let i = 0; i < Math.min(currentFilterCount, newCount); i++) {
      paneviewApi.getPanel(`filter-${i}`)?.api
        .updateParameters({ filterBlock: childrenArray[i], onCollapseChange, isProgrammaticResizeRef });
    }

    // Add new panels for children that don't have a panel yet (inserted before the spacer)
    for (let i = currentFilterCount; i < newCount; i++) {
      const spacerIndex = paneviewApi.panels.findIndex((p) => p.id === SPACER_PANEL_ID);
      paneviewApi.addPanel({
        id: `filter-${i}`,
        component: 'filterBody',
        title: '',
        headerSize: 0,
        minimumBodySize: SINGLE_FILTER_HEADER_HEIGHT,
        isExpanded: true,
        params: { filterBlock: childrenArray[i], onCollapseChange, isProgrammaticResizeRef },
        index: spacerIndex >= 0 ? spacerIndex : undefined,
      });
    }

    // Remove excess panels when children count decreased
    for (let i = newCount; i < currentFilterCount; i++) {
      const panelId = `filter-${i}`;
      const panel = paneviewApi.getPanel(panelId);
      if (panel) {
        paneviewApi.removePanel(panel);
      }
      collapsedSetRef.current.delete(panelId);
    }

    // Invisible spacer panel absorbs remaining space so filter panels stack at the top
    if (!paneviewApi.getPanel(SPACER_PANEL_ID)) {
      paneviewApi.addPanel({
        id: SPACER_PANEL_ID,
        component: 'spacer',
        title: '',
        headerSize: 0,
        minimumBodySize: 0,
        isExpanded: true,
        params: {},
        size: 0,
      });
    }
  }, [paneviewApi, childrenArray, onCollapseChange]);

  // Redistribute space when container is resized externally (e.g. parent panel resize)
  useEffect(() => {
    if (!paneviewApi || !containerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      // Wait for dockview to finish its internal layout before redistributing
      requestAnimationFrame(() => {
        redistributeExpandedPanels();
      });
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [paneviewApi, redistributeExpandedPanels]);

  // Manage sash visibility: sash is only interactive between two expanded filter panels
  useEffect(() => {
    if (!paneviewApi || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const sashes = container.querySelectorAll<HTMLElement>('.dv-sash');
    const panels = paneviewApi.panels;

    // Sash[i] is between panels[i] and panels[i+1]
    sashes.forEach((sash, index) => {
      const panelAbove = panels[index];
      const panelBelow = panels[index + 1];

      const isExpandedFilter = (panel: typeof panels[0] | undefined) =>
        !!panel &&
        panel.id !== SPACER_PANEL_ID &&
        !collapsedSetRef.current.has(panel.id);

      const shouldBeActive = isExpandedFilter(panelAbove) && isExpandedFilter(panelBelow);
      sash.classList.toggle('sash-hidden', !shouldBeActive);
    });
  }, [paneviewApi, sashUpdateTrigger]);

  if (childrenArray.length === 0) {
    return null;
  }

  return (
    <>
      <NavigationHeader
        component={'div'}
        title={t('text:common.filter.title')}
        action={(
          <Stack direction={'row'} spacing={0.5} sx={{
            alignItems: 'center',
          }}>
            {headerActions}
            {onClearAllFilters && (
              <ClearFilterButton
                onClick={onClearAllFilters}
                isShown={hasActiveFilters}
              />
            )}
          </Stack>
        )}
      />
      <StyledPaneviewContainer ref={containerRef} sx={{ height: '100%', flex: 1 }}>
        <PaneviewReact
          onReady={onReady}
          components={paneviewComponents}
          disableDnd={true}
          className={'dockview-theme-dark'}
        />
      </StyledPaneviewContainer>
    </>
  );
};
